import * as FilePond from 'filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import en_EN from 'filepond/locale/en-en';
import de_DE from 'filepond/locale/de-de';

const initApplicationForm = async applicationForm => {
    const session = await fetch('/actions/users/session-info', {
        headers: {
            Accept: 'application/json',
        },
    }).then(response => response.json());

    // Warn before leaving the page as soon as the user interacts with the form
    const navigationGuard = e => {
        console.log('beforeunload');
        e.preventDefault();
        return '';
    };
    const registerNavigationGuard = e => {
        applicationForm.removeEventListener('input', registerNavigationGuard);
        window.addEventListener('beforeunload', navigationGuard);
    };
    applicationForm.addEventListener('input', registerNavigationGuard);

    // Disable navigation guard when the form is submitted
    applicationForm.addEventListener('submit', () =>
        window.removeEventListener('beforeunload', navigationGuard),
    );

    const documentsInput = applicationForm.querySelector('input[name="applicationDocuments"]');

    FilePond.registerPlugin(FilePondPluginFileValidateType);
    FilePond.registerPlugin(FilePondPluginFileValidateSize);

    const language = document.documentElement.lang.slice(0, 2);
    const translations = { de: de_DE, en: en_EN };
    const customLabels = {
        de: {
            fileValidateTypeLabelExpectedTypes: 'Bitte laden Sie bevorzugt PDF-Dateien hoch.',
        },
        en: {
            fileValidateTypeLabelExpectedTypes: 'Please upload PDF files, if possible.',
        },
    };
    const labels = {
        ...(translations[language] || translations.de),
        ...(customLabels[language] || customLabels.de),
    };

    // Restore files that were uploaded before the form was submitted, if the
    // form is displayed again because of validation errors.
    const restoredFilesData = documentsInput.dataset.restoredFiles;
    const restoredFiles = restoredFilesData ? JSON.parse(restoredFilesData) : [];
    const files = restoredFiles.map(fileData => ({
        source: fileData.uuid,
        options: {
            type: 'local',
            file: {
                name: fileData.original_filename,
                size: 0,
                type: 'N/A',
            },
        },
    }));

    const filepond = FilePond.create(documentsInput, {
        ...labels,
        files,
        maxParallelUploads: 1,
        itemInsertLocation: 'after',
        credits: false,

        // FilePondPluginFileValidateSize
        maxFileSize: '20MB',

        server: {
            process: {
                url: '/actions/personio/application/document',
                method: 'POST',
                headers: {
                    'X-CSRF-Token': session.csrfTokenValue,
                },
                onload: response => {
                    const json = JSON.parse(response);

                    // Insert additional hidden inputs to submit the received
                    // file UUIDs alongside their metadata to the application.
                    const namespace = `application[files][${json.uuid}]`;
                    const properties = Object.keys(json);
                    properties.forEach(property => {
                        const input = document.createElement('input');
                        input.type = 'hidden';
                        input.name = `${namespace}[${property}]`;
                        input.value = json[property];
                        applicationForm.appendChild(input);
                    });

                    return json.uuid;
                },
            },
            revert: {
                url: '/actions/personio/application/document',
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': session.csrfTokenValue,
                },
            },
        },

        // Save the UUID as metadata to work around this issue:
        // https://github.com/pqina/react-filepond/issues/26
        onprocessfile: (error, file) => {
            if (error) {
                return;
            }
            file.setMetadata('uuid', file.serverId, true);
        },

        // When a file is removed, delete the hidden inputs associated with it
        onremovefile: (error, file) => {
            if (error) {
                return;
            }
            const uuid = file.getMetadata('uuid');
            if (!uuid) {
                return;
            }
            const namespace = `application[files][${uuid}]`;
            const fileInputs = document.querySelectorAll(`[name^="${namespace}"]`);
            fileInputs.forEach(input => input.remove());
        },
    });

    window.filepond = filepond;

    // Select fields with an option to enter a custom value
    const customValueOptions = document.querySelectorAll('option[data-custom-option-for]');
    customValueOptions.forEach(option => {
        const select = option.parentElement;
        const customInputField = document.querySelector(
            `[data-custom-input-for="${option.dataset.customOptionFor}"]`,
        );
        select.addEventListener('change', e => {
            customInputField.hidden = !option.selected;
        });
    });
};

const applicationForm = document.querySelector('.application-form');
if (applicationForm) {
    initApplicationForm(applicationForm);
}
